import { useEffect, useState } from "react";
import { subscriptionService } from "@miplatform/api";
import { HTTP_STATUSES } from "@miplatform/constants";
import { useComponentState, useProfilePicture } from "@miplatform/hooks";
import {
  createUUID,
  externalReferenceId,
  getRootUrl,
  sortOnField,
} from "@miplatform/utils";

const getUserUUID = createUUID();
const updateUserUUID = createUUID();

export const useProfileViewModel = ({ userId }) => {
  const [user, setUser] = useState(null);
  const stateManager = useComponentState();

  const isLoading = stateManager.isBusy();

  const getUser = (id) => {
    stateManager.abort(getUserUUID);
    return stateManager.run(async () => {
      const response = await subscriptionService.users.get(id);
      if (response.status === HTTP_STATUSES.OK) {
        // pull out pin so it doesn't end up messing with validation.
        const { pin: pinHash, title, roles, ...data } = response.data;
        const normalizedUser = {
          ...data,
          pinHash,
          roleIds: roles.sort(sortOnField("name")).map((role) => role.id),
          title: title || "",
        };
        setUser(normalizedUser);
        return normalizedUser;
      }

      return null;
    }, getUserUUID);
  };

  const handleUpdateUser = async (data) => {
    stateManager.abort(updateUserUUID);
    return stateManager.run(async () => {
      const response = await subscriptionService.users.updateProfile({
        data,
        toastMessageConfig: {
          success: {
            text: `Profile updated successfully!`,
          },
          failure: {
            text: `Profile failed to update!`,
          },
        },
      });

      if (response.status === HTTP_STATUSES.OK) {
        const { pin: pinHash, ...data } = response.data;
        // same as line 26
        setUser({
          ...data,
          pinHash,
        });
      }
      return response;
    }, updateUserUUID);
  };

  useEffect(() => {
    const getUserById = async () => {
      if (userId) {
        await getUser(userId);
      }
    };

    // This is an acceptable convention in React,
    // but Sonar will still gripe about it.
    getUserById();
  }, [userId]);

  const {
    isLoading: profilePictureLoading,
    removePicture,
    uploadPicture,
  } = useProfilePicture({
    values: user,
    getExternalReferenceId: () =>
      user
        ? externalReferenceId.getProfilePhotoRefId({
            userId: user.id,
          })
        : null,
  });

  const uploadProfilePhoto = async (file) => {
    await deleteProfilePhoto();

    const response = await uploadPicture(file);
    if (response.status === HTTP_STATUSES.OK) {
      // Refresh user data to get the updated photo
      await getUser(user.id);
    }
  };

  const deleteProfilePhoto = async () => {
    if (!user?.photo?.id) {
      return;
    }

    const response = await removePicture();
    if (response.status === HTTP_STATUSES.OK) {
      // Refresh user data to reflect the deleted photo
      await getUser(user.id);
    }
  };

  const getProfilePhotoUrl = (documentId) => {
    if (!documentId) return null;
    return `${getRootUrl()}/document-service/documents/${documentId}`;
  };

  return {
    isLoading: isLoading || profilePictureLoading,
    user,
    handleUpdateUser,
    uploadProfilePhoto,
    deleteProfilePhoto,
    getProfilePhotoUrl,
  };
};
